/**
 * WEB-672 atom E — per-block font override, render + loading helpers shared
 * by all three block renderers (PublicBlockRenderer,
 * PublicBlockRendererExtended, BlockRendererExtended) and the font
 * preloaders (PublicQuiz / PublicPaywall / PhonePreview).
 *
 * Half-of-the-pair decision: the override uses
 * resolveFontPair(fontId).headingFamily for the WHOLE block — headingFamily
 * IS the family the id names, while bodyFamily is only the pairing partner
 * (mostly Inter). A user explicitly picking «Playfair Display» for a
 * paragraph must see Playfair, not Inter. Same decision as the countdown
 * digit font (countdownDigitFontId, WEB-696).
 */
import React from 'react';
import { resolveFontPair } from '@/themes/fonts';
import { fontSmoothingStyle } from '@/themes/fontSmoothing';

/**
 * Wrap a rendered block in a zero-layout font scope when block.fontId is set.
 *
 * `display: contents` generates no box (layout/margins untouched); the
 * override reaches the content two ways:
 *  - fontFamily — inherited by all text that doesn't set its own family
 *    (form controls get it via the [data-block-font] rule in global.css);
 *  - --font-family-heading / --font-family-primary — re-resolved by blocks
 *    whose inline style reads the vars (title/heading font chain, WEB-447 A3).
 *
 * No fontId (or nothing rendered) → the node is returned untouched, funnel
 * level inheritance stays exactly as before.
 */
export function withBlockFont(
  fontId: string | undefined,
  node: React.ReactElement | null,
): React.ReactElement | null;
export function withBlockFont(fontId: string | undefined, node: React.ReactNode): React.ReactNode;
export function withBlockFont(fontId: string | undefined, node: React.ReactNode): React.ReactNode {
  if (!fontId || node === null || node === undefined || node === false) {
    return node;
  }
  const family = resolveFontPair(fontId).headingFamily;
  const style = {
    display: 'contents',
    fontFamily: family,
    '--font-family-heading': family,
    '--font-family-primary': family,
    // Решение владельца №1: системный шрифт блока — со сглаживанием эталона.
    ...fontSmoothingStyle(fontId),
  } as React.CSSProperties;
  return (
    <div data-block-font={fontId} style={style}>
      {node}
    </div>
  );
}

interface FontCarryingBlock {
  fontId?: string;
  /** WEB-696 countdown digit font — shares the same catalogue. */
  countdownDigitFontId?: string;
}

interface FontCarryingScreen {
  blocks?: ReadonlyArray<FontCarryingBlock | null | undefined>;
}

/**
 * Unique font ids referenced by blocks across screens (block.fontId +
 * countdown's countdownDigitFontId, which was never preloaded before).
 * Callers pass each id to ensureFontsLoaded — without this the picked
 * per-block font renders as the fallback stack.
 */
export function collectBlockFontIds(
  screens: ReadonlyArray<FontCarryingScreen | null | undefined> | null | undefined,
): string[] {
  const ids = new Set<string>();
  for (const screen of screens ?? []) {
    for (const block of screen?.blocks ?? []) {
      if (block?.fontId) ids.add(block.fontId);
      if (block?.countdownDigitFontId) ids.add(block.countdownDigitFontId);
    }
  }
  return [...ids];
}
